import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useReducer, useState, type ReactElement } from 'react';
import ChartCardChartContainer, { ChartCardChartContainerSkeleton } from './components/ChartCardChartContainer';
import ChartCardError from './components/ChartCardError';
import ChartCardPerformanceContainer from './components/ChartCardPerformanceContainer';
import ChartCardTableContainer, { ChartCardTableContainerSkeleton } from './components/ChartCardTableContainer';
import { chartTypeToView } from './constants';
import { chartCardStateReducer, useInitChartCardStateFromValidatedProps } from './hooks';
import { useChartCardConstants } from './hooks/useChartCardConstants';
import { DEFAULT_TABLE_PAGE_SIZE, useChartCardFetchQuery } from './hooks/useChartCardFetchQuery';
import { useDeepCompareMemoize } from './hooks/generics';
import { useValidateChartCardProps } from './hooks/useValidateChartCardProps';
import useValidateChartDataAndDefinitionCommonProps from './hooks/useValidateChartDataAndDefinitionCommonProps';
import type { ChartCardCardsProps, ChartCardClientDataProps, ChartCardDataProps, ChartCardProps, ChartCardType, ChartDataRow, DefaultDrawingInnerProps } from './types';
import { isChartCardClientDataProps, isChartCardFetchDataProps } from './types';
import { validateChangeCardType } from './utils/card';
import { dataIsReady } from './utils/data';
import { defaultTFunction, errorLocal, objectEntries } from './utils/generics';
import { getSelectedDimensionColumnIds } from './utils/columnsSelection';
import { getInitialSortingFromChartCardTableColumns } from './components/table/utils';
const EMPTY_CLIENT_DATA: ChartCardClientDataProps<string, ChartDataRow<string>> = {
  data: [],
  isLoading: false,
  isError: false
};
const getDataAndDataCompare = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  isFetchData,
  clientData,
  dataCompare,
  fetchData,
  fetchDataCompare
}: {
  isFetchData: boolean;
  clientData: ChartCardClientDataProps<DataKey, DataRow>;
  dataCompare: ChartCardDataProps<DataKey, DataRow> | undefined;
  fetchData: DataRow[];
  fetchDataCompare: DataRow[] | undefined;
}): {
  data: DataRow[];
  dataCompare: DataRow[] | undefined;
} => {
  if (isFetchData) {
    return {
      data: fetchData,
      dataCompare: fetchDataCompare
    };
  }
  return {
    data: clientData.data ?? [],
    dataCompare: dataCompare != null && isChartCardClientDataProps(dataCompare) ? dataCompare.data ?? undefined : undefined
  };
};
const ChartCard = t0 => {
  const $ = _c(221);
  const {
    card: cardProp,
    data: dataPropFromParent,
    dataCompare: dataCompareProp,
    dataDefinition: dataDefinitionProp,
    mutable: mutableProp,
    customisations
  } = t0;
  const [cardType, setCardType] = useState(cardProp.type);
  let t1;
  if ($[0] !== dataPropFromParent) {
    t1 = isChartCardFetchDataProps(dataPropFromParent);
    $[0] = dataPropFromParent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isFetchData = t1;
  let t2;
  if ($[2] !== customisations?.usePagination) {
    t2 = () => customisations?.usePagination ?? false;
    $[2] = customisations?.usePagination;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [usePagination, setUsePagination] = useState(t2);
  const isServerFetch = isFetchData && cardType === "table" && usePagination;
  let t3;
  if ($[4] !== customisations?.usePagination) {
    t3 = () => {
      setUsePagination(customisations?.usePagination ?? false);
    };
    $[4] = customisations?.usePagination;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = customisations?.usePagination;
  let t5;
  if ($[6] !== t4) {
    t5 = [t4];
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t3, t5);
  if (isFetchData && dataPropFromParent.fetchData == null) {
    errorLocal("ChartCard fetch data source requires fetchData", {
      id: cardProp.id
    });
  }
  let t6;
  bb0: {
    if (isChartCardFetchDataProps(dataPropFromParent)) {
      t6 = EMPTY_CLIENT_DATA as ChartCardClientDataProps<DataKey, DataRow>;
      break bb0;
    }
    t6 = dataPropFromParent;
  }
  const dataProp = t6;
  const enableNullDimensionValue = customisations?.enableNullDimensionValue ?? true;
  let t7;
  if ($[8] !== enableNullDimensionValue || $[9] !== isFetchData) {
    t7 = {
      allowEmptyBootstrap: isFetchData,
      enableNullDimensionValue
    };
    $[8] = enableNullDimensionValue;
    $[9] = isFetchData;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const validatedDataDefinitionProps = useValidateChartDataAndDefinitionCommonProps(dataDefinitionProp, dataProp, t7);
  customisations?.allowChangeCardType;
  const t8 = customisations?.allowChangeCardType;
  let t9;
  if ($[11] !== cardProp || $[12] !== cardType || $[13] !== t8) {
    t9 = validateChangeCardType({
      cardTypeSelected: cardType,
      cardProp,
      allowChangeCardType: t8
    });
    $[11] = cardProp;
    $[12] = cardType;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const {
    card,
    otherCardTypes
  } = t9;
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      state: "initialising"
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const [state, dispatch] = useReducer(chartCardStateReducer, t10);
  let t11;
  if ($[16] !== card || $[17] !== dataProp || $[18] !== validatedDataDefinitionProps) {
    t11 = {
      dataDefinition: validatedDataDefinitionProps,
      card,
      data: dataProp
    };
    $[16] = card;
    $[17] = dataProp;
    $[18] = validatedDataDefinitionProps;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const validatedProps = useValidateChartCardProps(t11);
  const stableSelectionsProp = useDeepCompareMemoize(mutableProp.selections);
  const stableDateRangeProp = useDeepCompareMemoize(mutableProp.dateRange);
  let t12;
  if ($[20] !== dataPropFromParent || $[21] !== isFetchData) {
    t12 = isFetchData ? {
      key: dataPropFromParent.fetchResetKey
    } : undefined;
    $[20] = dataPropFromParent;
    $[21] = isFetchData;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const stableFetchResetKeyProp = useDeepCompareMemoize(t12);
  useInitChartCardStateFromValidatedProps(validatedProps, dispatch);
  let t13;
  let t14;
  if ($[23] !== stableSelectionsProp || $[24] !== state.state) {
    t13 = () => {
      if (state.state !== "initialising") {
        dispatch({
          type: "updateSelections",
          selections: stableSelectionsProp
        });
      }
    };
    t14 = [stableSelectionsProp, state.state];
    $[23] = stableSelectionsProp;
    $[24] = state.state;
    $[25] = t13;
    $[26] = t14;
  } else {
    t13 = $[25];
    t14 = $[26];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[27] !== stableDateRangeProp || $[28] !== state.state) {
    t15 = () => {
      if (state.state !== "initialising") {
        dispatch({
          type: "updateDateRange",
          dateRange: stableDateRangeProp
        });
      }
    };
    t16 = [stableDateRangeProp, state.state];
    $[27] = stableDateRangeProp;
    $[28] = state.state;
    $[29] = t15;
    $[30] = t16;
  } else {
    t15 = $[29];
    t16 = $[30];
  }
  useEffect(t15, t16);
  let t17;
  if ($[31] !== customisations) {
    t17 = customisations ?? {};
    $[31] = customisations;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const {
    onSelectionsUpdated,
    drawingColorMaps: drawingColorMapsProp,
    chartDrawing: chartDrawingProp
  } = t17;
  let t18;
  let t19;
  if ($[33] !== cardProp.id || $[34] !== onSelectionsUpdated || $[35] !== state.selections) {
    t18 = () => {
      if (state.selections != null && onSelectionsUpdated != null) {
        onSelectionsUpdated?.(state.selections, cardProp.id);
      }
    };
    t19 = [state.selections, onSelectionsUpdated, cardProp.id];
    $[33] = cardProp.id;
    $[34] = onSelectionsUpdated;
    $[35] = state.selections;
    $[36] = t18;
    $[37] = t19;
  } else {
    t18 = $[36];
    t19 = $[37];
  }
  useEffect(t18, t19);
  const {
    defaults
  } = useChartCardConstants();
  let t20;
  if ($[38] !== card.type || $[39] !== chartDrawingProp || $[40] !== defaults.cardDrawing || $[41] !== defaults.drawing) {
    let t21;
    if ($[43] !== card.type || $[44] !== chartDrawingProp || $[45] !== defaults.cardDrawing) {
      t21 = t22 => {
        const [key, value] = t22;
        return [key, {
          ...value,
          ...defaults.cardDrawing[card.type]?.[key],
          ...chartDrawingProp?.[key]
        }];
      };
      $[43] = card.type;
      $[44] = chartDrawingProp;
      $[45] = defaults.cardDrawing;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    t20 = Object.fromEntries(objectEntries(defaults.drawing).map(t21));
    $[38] = card.type;
    $[39] = chartDrawingProp;
    $[40] = defaults.cardDrawing;
    $[41] = defaults.drawing;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const chartDrawing = useDeepCompareMemoize(t20 as DefaultDrawingInnerProps);
  const t21 = drawingColorMapsProp?.primary;
  let t22;
  if ($[47] !== defaults.drawingColorMaps.primary || $[48] !== t21) {
    t22 = {
      ...defaults.drawingColorMaps.primary,
      ...t21
    };
    $[47] = defaults.drawingColorMaps.primary;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = drawingColorMapsProp?.secondary;
  let t24;
  if ($[50] !== defaults.drawingColorMaps.secondary || $[51] !== t23) {
    t24 = {
      ...defaults.drawingColorMaps.secondary,
      ...t23
    };
    $[50] = defaults.drawingColorMaps.secondary;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t22 || $[54] !== t24) {
    t25 = {
      primary: t22,
      secondary: t24
    };
    $[53] = t22;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const drawingColorMaps = useDeepCompareMemoize(t25);
  let t26;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = cardType_0 => {
      dispatch({
        type: "checkCardType",
        cardType: cardType_0
      });
      setCardType(cardType_0);
    };
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  const changeCardType = t26;
  let t27;
  if ($[57] !== isServerFetch) {
    t27 = () => {
      if (isServerFetch) {
        return;
      }
      setUsePagination(_temp);
    };
    $[57] = isServerFetch;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  const toggleUsePagination = t27;
  const t = cardProp.t ?? defaultTFunction;
  customisations?.useDefaultSorting;
  let t28;
  bb1: {
    if (validatedProps.result !== "validTable") {
      let t29;
      if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
        t29 = [];
        $[59] = t29;
      } else {
        t29 = $[59];
      }
      t28 = t29;
      break bb1;
    }
    const t29 = customisations?.useDefaultSorting ?? true;
    let t30;
    if ($[60] !== t29 || $[61] !== validatedProps.card.columns || $[62] !== validatedProps.elementsDescriptionsMap) {
      t30 = getInitialSortingFromChartCardTableColumns({
        useDefaultSorting: t29,
        columns: validatedProps.card.columns,
        elementsDescriptionsMap: validatedProps.elementsDescriptionsMap
      });
      $[60] = t29;
      $[61] = validatedProps.card.columns;
      $[62] = validatedProps.elementsDescriptionsMap;
      $[63] = t30;
    } else {
      t30 = $[63];
    }
    t28 = t30;
  }
  const initialSorting = t28;
  let t29;
  if ($[64] !== stableDateRangeProp || $[65] !== stableFetchResetKeyProp || $[66] !== stableSelectionsProp) {
    t29 = JSON.stringify({
      selections: stableSelectionsProp,
      dateRange: stableDateRangeProp,
      external: stableFetchResetKeyProp
    });
    $[64] = stableDateRangeProp;
    $[65] = stableFetchResetKeyProp;
    $[66] = stableSelectionsProp;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  const fetchResetKey = t29;
  let t30;
  if ($[68] !== isServerFetch || $[69] !== state.selections || $[70] !== validatedProps.elementsDescriptionsMap || $[71] !== validatedProps.result) {
    t30 = isServerFetch && validatedProps.result === "validTable" ? getSelectedDimensionColumnIds({
      selectedColumnsIds: state.selections?.["columns" as DataKey] as DataKey[] | undefined,
      elementsDescriptionsMap: validatedProps.elementsDescriptionsMap
    }) : undefined;
    $[68] = isServerFetch;
    $[69] = state.selections;
    $[70] = validatedProps.elementsDescriptionsMap;
    $[71] = validatedProps.result;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  const fetchDimensions = useDeepCompareMemoize(t30);
  const fetchDataFn = isFetchData ? dataPropFromParent.fetchData : undefined;
  const fetchCompareDataFn = isFetchData && dataCompareProp != null && isChartCardFetchDataProps(dataCompareProp) ? dataCompareProp.fetchData : undefined;
  const fetchInitialPageSize = isFetchData ? dataPropFromParent.initialPageSize ?? DEFAULT_TABLE_PAGE_SIZE : DEFAULT_TABLE_PAGE_SIZE;
  const fetchCompareInitialPageSize = isFetchData && dataCompareProp != null && isChartCardFetchDataProps(dataCompareProp) ? dataCompareProp.initialPageSize ?? fetchInitialPageSize : fetchInitialPageSize;
  const definitionReadyForFetch = validatedProps.result === "validTable" || validatedProps.result === "validChart";
  const t31 = isFetchData && definitionReadyForFetch && state.state === "valid";
  let t32;
  if ($[73] !== fetchDataFn || $[74] !== fetchDimensions || $[75] !== fetchInitialPageSize || $[76] !== fetchResetKey || $[77] !== initialSorting || $[78] !== isServerFetch || $[79] !== t31) {
    t32 = {
      enabled: t31,
      fetchData: fetchDataFn,
      usePagination: isServerFetch,
      initialPageSize: fetchInitialPageSize,
      initialSorting,
      resetKey: fetchResetKey,
      dimensions: fetchDimensions
    };
    $[73] = fetchDataFn;
    $[74] = fetchDimensions;
    $[75] = fetchInitialPageSize;
    $[76] = fetchResetKey;
    $[77] = initialSorting;
    $[78] = isServerFetch;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  const fetchQuery = useChartCardFetchQuery(t32);
  const t33 = isFetchData && fetchCompareDataFn != null && definitionReadyForFetch && state.state === "valid";
  const t34 = isServerFetch ? fetchQuery.pagination : undefined;
  const t35 = isServerFetch ? fetchQuery.sorting : undefined;
  let t36;
  if ($[81] !== fetchCompareDataFn || $[82] !== fetchCompareInitialPageSize || $[83] !== fetchDimensions || $[84] !== fetchResetKey || $[85] !== initialSorting || $[86] !== isServerFetch || $[87] !== t33 || $[88] !== t34 || $[89] !== t35) {
    t36 = {
      enabled: t33,
      fetchData: fetchCompareDataFn,
      usePagination: isServerFetch,
      initialPageSize: fetchCompareInitialPageSize,
      initialSorting,
      resetKey: fetchResetKey,
      dimensions: fetchDimensions,
      externalPagination: t34,
      externalSorting: t35
    };
    $[81] = fetchCompareDataFn;
    $[82] = fetchCompareInitialPageSize;
    $[83] = fetchDimensions;
    $[84] = fetchResetKey;
    $[85] = initialSorting;
    $[86] = isServerFetch;
    $[87] = t33;
    $[88] = t34;
    $[89] = t35;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  const fetchCompareQuery = useChartCardFetchQuery(t36);
  const useFuzzySearch = isServerFetch ? false : customisations?.useFuzzySearch;
  const onOpenColumnFilter = customisations?.onOpenColumnFilter;
  const allowColumnFiltering = isServerFetch && onOpenColumnFilter == null ? false : customisations?.allowColumnFiltering;
  const allowDateFiltering = isServerFetch ? false : customisations?.allowDateFiltering;
  const allowToggleUsePagination = isServerFetch ? false : customisations?.allowToggleUsePagination;
  const allowExport = isServerFetch ? false : customisations?.allowExport;
  const fetchCompareReady = fetchCompareDataFn == null || !fetchCompareQuery.isError && (fetchCompareQuery.data.length > 0 || !fetchCompareQuery.isLoading);
  const fetchReady = isFetchData && definitionReadyForFetch && state.state === "valid" && fetchDataFn != null && !fetchQuery.isError && (fetchQuery.data.length > 0 || !fetchQuery.isLoading) && fetchCompareReady;
  const t37 = fetchCompareDataFn != null ? fetchCompareQuery.data : undefined;
  let t38;
  if ($[91] !== dataCompareProp || $[92] !== dataProp || $[93] !== fetchQuery.data || $[94] !== isFetchData || $[95] !== t37) {
    t38 = getDataAndDataCompare({
      isFetchData,
      clientData: dataProp,
      dataCompare: dataCompareProp,
      fetchData: fetchQuery.data,
      fetchDataCompare: t37
    });
    $[91] = dataCompareProp;
    $[92] = dataProp;
    $[93] = fetchQuery.data;
    $[94] = isFetchData;
    $[95] = t37;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  const {
    data: resolvedData,
    dataCompare: resolvedDataCompare
  } = t38;
  if (card.type === "table") {
    const clientReady = !isFetchData && validatedProps.result === "validTable" && state.state === "valid" && dataIsReady(dataProp) && (dataCompareProp == null || dataIsReady(dataCompareProp));
    if (fetchReady && validatedProps.result === "validTable" || clientReady) {
      if (validatedProps.result !== "validTable" || state.state !== "valid") {
        const t39 = customisations?.cardSize;
        const t40 = customisations?.tableSize;
        let t41;
        if ($[97] !== t39 || $[98] !== t40) {
          t41 = <ChartCardTableContainerSkeleton view="table" cardSize={t39} tableSize={t40} />;
          $[97] = t39;
          $[98] = t40;
          $[99] = t41;
        } else {
          t41 = $[99];
        }
        return t41;
      }
      const t39 = customisations?.cardSize;
      const t40 = customisations?.tableSize;
      const t41 = customisations?.downloadTitle;
      const t42 = customisations?.expandedToggle;
      const t43 = customisations?.allowResetCard;
      const t44 = customisations?.useDefaultSorting;
      const t45 = customisations?.useDefaultPinning;
      const t46 = customisations?.useColumnsDecorations;
      const t47 = customisations?.useBodyScrollDecoration;
      const t48 = customisations?.useOverscrollContain;
      const t49 = customisations?.useDebugTools;
      const t50 = customisations?.allowRowsSelection;
      const t51 = customisations?.allowMultipleColumnsSorting;
      const t52 = customisations?.allowColumnsSelection;
      const t53 = customisations?.allowColumnResizing;
      const t54 = customisations?.allowColumnAutoResizing;
      const t55 = customisations?.allowColumnPinning;
      const t56 = customisations?.allowColumnReordering;
      const t57 = customisations?.actions;
      const t58 = customisations?.moreMenuActions;
      const t59 = isServerFetch ? fetchQuery.totalRows : undefined;
      const t60 = isServerFetch ? fetchQuery.totals : undefined;
      const t61 = isServerFetch ? fetchQuery.pagination : undefined;
      const t62 = isServerFetch ? fetchQuery.sorting : undefined;
      const t63 = isServerFetch ? fetchQuery.setPagination : undefined;
      const t64 = isServerFetch ? fetchQuery.setSorting : undefined;
      const t65 = isFetchData ? fetchQuery.isLoading || fetchCompareQuery.isLoading : undefined;
      let t66;
      if ($[100] !== allowColumnFiltering || $[101] !== allowDateFiltering || $[102] !== allowExport || $[103] !== allowToggleUsePagination || $[104] !== cardProp.id || $[105] !== cardProp.title || $[106] !== enableNullDimensionValue || $[107] !== isServerFetch || $[108] !== onOpenColumnFilter || $[109] !== otherCardTypes || $[110] !== resolvedData || $[111] !== resolvedDataCompare || $[112] !== state || $[113] !== t || $[114] !== t39 || $[115] !== t40 || $[116] !== t41 || $[117] !== t42 || $[118] !== t43 || $[119] !== t44 || $[120] !== t45 || $[121] !== t46 || $[122] !== t47 || $[123] !== t48 || $[124] !== t49 || $[125] !== t50 || $[126] !== t51 || $[127] !== t52 || $[128] !== t53 || $[129] !== t54 || $[130] !== t55 || $[131] !== t56 || $[132] !== t57 || $[133] !== t58 || $[134] !== t59 || $[135] !== t60 || $[136] !== t61 || $[137] !== t62 || $[138] !== t63 || $[139] !== t64 || $[140] !== t65 || $[141] !== toggleUsePagination || $[142] !== useFuzzySearch || $[143] !== usePagination || $[144] !== validatedProps.card.columns || $[145] !== validatedProps.dimensionsMap || $[146] !== validatedProps.dimensionsValuesMap || $[147] !== validatedProps.elementsDescriptionsMap || $[148] !== validatedProps.metricsMap || $[149] !== validatedProps.metricsVariantMap) {
        t66 = <ChartCardTableContainer view="table" id={cardProp.id} title={cardProp.title} t={t} cardSize={t39} tableSize={t40} data={resolvedData} dataCompare={resolvedDataCompare} dimensionsMap={validatedProps.dimensionsMap} metricsVariantMap={validatedProps.metricsVariantMap} metricsMap={validatedProps.metricsMap} dimensionsValuesMap={validatedProps.dimensionsValuesMap} columns={validatedProps.card.columns} elementsDescriptionsMap={validatedProps.elementsDescriptionsMap} state={state} dispatch={dispatch} downloadTitle={t41} expandedToggle={t42} allowResetCard={t43} allowExport={allowExport} usePagination={usePagination} useFuzzySearch={useFuzzySearch} useDefaultSorting={t44} useDefaultPinning={t45} useColumnsDecorations={t46} useBodyScrollDecoration={t47} useOverscrollContain={t48} useDebugTools={t49} allowToggleUsePagination={allowToggleUsePagination} allowRowsSelection={t50} allowMultipleColumnsSorting={t51} allowColumnsSelection={t52} allowColumnFiltering={allowColumnFiltering} allowDateFiltering={allowDateFiltering} onOpenColumnFilter={onOpenColumnFilter} allowColumnResizing={t53} allowColumnAutoResizing={t54} allowColumnPinning={t55} allowColumnReordering={t56} enableNullDimensionValue={enableNullDimensionValue} isServerFetch={isServerFetch} actions={t57} moreMenuActions={t58} otherCardTypes={otherCardTypes} toggleUsePagination={toggleUsePagination} changeCardType={changeCardType} serverTotalRows={t59} serverTotals={t60} serverPagination={t61} serverSorting={t62} onServerPaginationChange={t63} onServerSortingChange={t64} isFetchLoading={t65} />;
        $[100] = allowColumnFiltering;
        $[101] = allowDateFiltering;
        $[102] = allowExport;
        $[103] = allowToggleUsePagination;
        $[104] = cardProp.id;
        $[105] = cardProp.title;
        $[106] = enableNullDimensionValue;
        $[107] = isServerFetch;
        $[108] = onOpenColumnFilter;
        $[109] = otherCardTypes;
        $[110] = resolvedData;
        $[111] = resolvedDataCompare;
        $[112] = state;
        $[113] = t;
        $[114] = t39;
        $[115] = t40;
        $[116] = t41;
        $[117] = t42;
        $[118] = t43;
        $[119] = t44;
        $[120] = t45;
        $[121] = t46;
        $[122] = t47;
        $[123] = t48;
        $[124] = t49;
        $[125] = t50;
        $[126] = t51;
        $[127] = t52;
        $[128] = t53;
        $[129] = t54;
        $[130] = t55;
        $[131] = t56;
        $[132] = t57;
        $[133] = t58;
        $[134] = t59;
        $[135] = t60;
        $[136] = t61;
        $[137] = t62;
        $[138] = t63;
        $[139] = t64;
        $[140] = t65;
        $[141] = toggleUsePagination;
        $[142] = useFuzzySearch;
        $[143] = usePagination;
        $[144] = validatedProps.card.columns;
        $[145] = validatedProps.dimensionsMap;
        $[146] = validatedProps.dimensionsValuesMap;
        $[147] = validatedProps.elementsDescriptionsMap;
        $[148] = validatedProps.metricsMap;
        $[149] = validatedProps.metricsVariantMap;
        $[150] = t66;
      } else {
        t66 = $[150];
      }
      return t66;
    }
    if (!isFetchData && (dataProp.isError || dataCompareProp?.isError === true) || isFetchData && (fetchQuery.isError || fetchCompareQuery.isError)) {
      const t39 = customisations?.cardSize;
      const t40 = customisations?.chartSize;
      const t41 = customisations?.tableSize;
      let t42;
      if ($[151] !== t || $[152] !== t39 || $[153] !== t40 || $[154] !== t41) {
        t42 = <ChartCardError view="table" cardSize={t39} chartSize={t40} tableSize={t41} type="dataError" t={t} />;
        $[151] = t;
        $[152] = t39;
        $[153] = t40;
        $[154] = t41;
        $[155] = t42;
      } else {
        t42 = $[155];
      }
      return t42;
    }
    const t39 = customisations?.cardSize;
    const t40 = customisations?.tableSize;
    let t41;
    if ($[156] !== t39 || $[157] !== t40) {
      t41 = <ChartCardTableContainerSkeleton view="table" cardSize={t39} tableSize={t40} />;
      $[156] = t39;
      $[157] = t40;
      $[158] = t41;
    } else {
      t41 = $[158];
    }
    return t41;
  }
  const view = chartTypeToView[card.type === "performance" ? card.performance.type : card.chart.type];
  const clientChartReady = !isFetchData && validatedProps.result === "validChart" && state.state === "valid" && dataIsReady(dataProp) && (dataCompareProp == null || dataIsReady(dataCompareProp));
  if (fetchReady && validatedProps.result === "validChart" || clientChartReady) {
    if (validatedProps.result !== "validChart" || state.state !== "valid") {
      const t39 = customisations?.cardSize;
      const t40 = customisations?.chartSize;
      let t41;
      if ($[159] !== t39 || $[160] !== t40 || $[161] !== view) {
        t41 = <ChartCardChartContainerSkeleton cardSize={t39} chartSize={t40} view={view} />;
        $[159] = t39;
        $[160] = t40;
        $[161] = view;
        $[162] = t41;
      } else {
        t41 = $[162];
      }
      return t41;
    }
    if (validatedProps.card.type === "performance") {
      const t39 = customisations?.cardSize;
      const t40 = customisations?.chartSize;
      const t41 = customisations?.respectColors;
      const t42 = customisations?.hideChart;
      const t43 = customisations?.hideTotalValue;
      let t44;
      if ($[163] !== cardProp.id || $[164] !== cardProp.title || $[165] !== chartDrawing || $[166] !== drawingColorMaps || $[167] !== otherCardTypes || $[168] !== resolvedData || $[169] !== resolvedDataCompare || $[170] !== state || $[171] !== t || $[172] !== t39 || $[173] !== t40 || $[174] !== t41 || $[175] !== t42 || $[176] !== t43 || $[177] !== validatedProps.card.performance || $[178] !== validatedProps.dimensionsMap || $[179] !== validatedProps.elementsDescriptionsMap || $[180] !== validatedProps.metricsMap) {
        t44 = <ChartCardPerformanceContainer id={cardProp.id} view="performance" title={cardProp.title} cardSize={t39} chartSize={t40} data={resolvedData} dataCompare={resolvedDataCompare} dimensionsMap={validatedProps.dimensionsMap} metricsMap={validatedProps.metricsMap} chart={validatedProps.card.performance} elementsDescriptionsMap={validatedProps.elementsDescriptionsMap} state={state} dispatch={dispatch} t={t} respectColors={t41} hideChart={t42} hideTotalValue={t43} chartDrawing={chartDrawing} drawingColorMaps={drawingColorMaps} otherCardTypes={otherCardTypes} changeCardType={changeCardType} />;
        $[163] = cardProp.id;
        $[164] = cardProp.title;
        $[165] = chartDrawing;
        $[166] = drawingColorMaps;
        $[167] = otherCardTypes;
        $[168] = resolvedData;
        $[169] = resolvedDataCompare;
        $[170] = state;
        $[171] = t;
        $[172] = t39;
        $[173] = t40;
        $[174] = t41;
        $[175] = t42;
        $[176] = t43;
        $[177] = validatedProps.card.performance;
        $[178] = validatedProps.dimensionsMap;
        $[179] = validatedProps.elementsDescriptionsMap;
        $[180] = validatedProps.metricsMap;
        $[181] = t44;
      } else {
        t44 = $[181];
      }
      return t44;
    }
    const t39 = customisations?.cardSize;
    const t40 = customisations?.chartSize;
    const t41 = customisations?.respectColors;
    const t42 = customisations?.axisDrawing;
    const t43 = customisations?.downloadTitle;
    const t44 = customisations?.expandedToggle;
    const t45 = customisations?.useDebugTools;
    const t46 = customisations?.allowResetCard;
    const t47 = customisations?.allowExport;
    const t48 = customisations?.tooltipContent;
    const t49 = customisations?.allowZooming;
    const t50 = customisations?.actions;
    const t51 = customisations?.moreMenuActions;
    let t52;
    if ($[182] !== cardProp.id || $[183] !== cardProp.title || $[184] !== chartDrawing || $[185] !== drawingColorMaps || $[186] !== otherCardTypes || $[187] !== resolvedData || $[188] !== resolvedDataCompare || $[189] !== state || $[190] !== t || $[191] !== t39 || $[192] !== t40 || $[193] !== t41 || $[194] !== t42 || $[195] !== t43 || $[196] !== t44 || $[197] !== t45 || $[198] !== t46 || $[199] !== t47 || $[200] !== t48 || $[201] !== t49 || $[202] !== t50 || $[203] !== t51 || $[204] !== validatedProps.card.chart || $[205] !== validatedProps.dimensionsMap || $[206] !== validatedProps.dimensionsValuesMap || $[207] !== validatedProps.elementsDescriptionsMap || $[208] !== validatedProps.metricsMap || $[209] !== validatedProps.metricsVariantMap) {
      t52 = <ChartCardChartContainer id={cardProp.id} view="chart" title={cardProp.title} t={t} cardSize={t39} chartSize={t40} data={resolvedData} dataCompare={resolvedDataCompare} dimensionsMap={validatedProps.dimensionsMap} metricsVariantMap={validatedProps.metricsVariantMap} metricsMap={validatedProps.metricsMap} dimensionsValuesMap={validatedProps.dimensionsValuesMap} chart={validatedProps.card.chart} elementsDescriptionsMap={validatedProps.elementsDescriptionsMap} state={state} dispatch={dispatch} respectColors={t41} chartDrawing={chartDrawing} drawingColorMaps={drawingColorMaps} axisDrawing={t42} downloadTitle={t43} expandedToggle={t44} useDebugTools={t45} allowResetCard={t46} allowExport={t47} tooltipContent={t48} allowZooming={t49} actions={t50} moreMenuActions={t51} otherCardTypes={otherCardTypes} changeCardType={changeCardType} />;
      $[182] = cardProp.id;
      $[183] = cardProp.title;
      $[184] = chartDrawing;
      $[185] = drawingColorMaps;
      $[186] = otherCardTypes;
      $[187] = resolvedData;
      $[188] = resolvedDataCompare;
      $[189] = state;
      $[190] = t;
      $[191] = t39;
      $[192] = t40;
      $[193] = t41;
      $[194] = t42;
      $[195] = t43;
      $[196] = t44;
      $[197] = t45;
      $[198] = t46;
      $[199] = t47;
      $[200] = t48;
      $[201] = t49;
      $[202] = t50;
      $[203] = t51;
      $[204] = validatedProps.card.chart;
      $[205] = validatedProps.dimensionsMap;
      $[206] = validatedProps.dimensionsValuesMap;
      $[207] = validatedProps.elementsDescriptionsMap;
      $[208] = validatedProps.metricsMap;
      $[209] = validatedProps.metricsVariantMap;
      $[210] = t52;
    } else {
      t52 = $[210];
    }
    return t52;
  }
  if (!isFetchData && (dataProp.isError || dataCompareProp?.isError === true) || isFetchData && (fetchQuery.isError || fetchCompareQuery.isError)) {
    const t39 = customisations?.cardSize;
    const t40 = customisations?.chartSize;
    const t41 = customisations?.tableSize;
    let t42;
    if ($[211] !== t || $[212] !== t39 || $[213] !== t40 || $[214] !== t41 || $[215] !== view) {
      t42 = <ChartCardError view={view} cardSize={t39} chartSize={t40} tableSize={t41} type="dataError" t={t} />;
      $[211] = t;
      $[212] = t39;
      $[213] = t40;
      $[214] = t41;
      $[215] = view;
      $[216] = t42;
    } else {
      t42 = $[216];
    }
    return t42;
  }
  const t39 = customisations?.cardSize;
  const t40 = customisations?.chartSize;
  let t41;
  if ($[217] !== t39 || $[218] !== t40 || $[219] !== view) {
    t41 = <ChartCardChartContainerSkeleton cardSize={t39} chartSize={t40} view={view} />;
    $[217] = t39;
    $[218] = t40;
    $[219] = view;
    $[220] = t41;
  } else {
    t41 = $[220];
  }
  return t41;
};
export default ChartCard;
function _temp(prev) {
  return !prev;
}